import { useState } from 'react'
import { X } from 'lucide-react'
import TimeRangeSlider from './TimeRangeSlider'
import type { Carrier } from '@/lib/api'

interface ScheduleEditorModalProps {
  carrier: Carrier | null
  onClose: () => void
  onSave: (carrierId: number, data: {
    inactive_start_time?: string
    inactive_end_time?: string
    always_active?: boolean
  }) => void
  isSaving?: boolean
}

export default function ScheduleEditorModal({
  carrier,
  onClose,
  onSave,
  isSaving = false
}: ScheduleEditorModalProps) {
  if (!carrier) return null

  const [alwaysActive, setAlwaysActive] = useState(carrier.schedule?.always_active ?? false)
  const [inactiveStart, setInactiveStart] = useState(carrier.schedule?.inactive_start_time ?? '13:00')
  const [inactiveEnd, setInactiveEnd] = useState(carrier.schedule?.inactive_end_time ?? '16:00')

  const handleSave = () => {
    onSave(carrier.id, {
      inactive_start_time: inactiveStart,
      inactive_end_time: inactiveEnd,
      always_active: alwaysActive
    })
  }

  const handleTimeRangeChange = (start: string, end: string) => {
    setInactiveStart(start)
    setInactiveEnd(end)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Editar Horario
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              {carrier.name}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Always Active Toggle */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <label className="flex items-start cursor-pointer">
              <div className="flex items-center h-6">
                <input
                  type="checkbox"
                  checked={alwaysActive}
                  onChange={(e) => setAlwaysActive(e.target.checked)}
                  disabled={isSaving}
                  className="w-5 h-5 text-primary-600 bg-slate-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-slate-700 dark:border-gray-600 cursor-pointer disabled:opacity-50"
                />
              </div>
              <div className="ml-3">
                <span className="text-sm font-medium text-slate-900 dark:text-white">
                  Activo 24 horas
                </span>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  El transportista estará siempre disponible, sin periodos de inactividad
                </p>
              </div>
            </label>
          </div>

          {/* Time Range Slider - Disabled when always active */}
          <div className={alwaysActive ? 'opacity-50 pointer-events-none' : ''}>
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                Periodo de Inactividad
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Define el horario en el que el transportista NO debe estar disponible (ej: durante la recogida de paquetes)
              </p>
            </div>

            <TimeRangeSlider
              startTime={inactiveStart}
              endTime={inactiveEnd}
              onChange={handleTimeRangeChange}
              disabled={alwaysActive || isSaving}
            />
          </div>

          {/* Info Box */}
          <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
              ¿Cómo funciona?
            </h4>
            <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
              <li>
                Durante el periodo <span className="font-semibold text-red-600 dark:text-red-400">INACTIVO</span>,
                el transportista se desactiva automáticamente
              </li>
              <li>
                Fuera de este horario, el transportista se activa para recibir pedidos del día siguiente
              </li>
              <li>
                Si marcas "Activo 24h", el transportista siempre estará disponible
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Guardando...
              </>
            ) : (
              'Guardar Cambios'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
